/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IGroup, IGroupDelivery, IGroups, IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/common/';
import {
    IHeader,
    IList,
    IOrderHistoryViewProps
} from '@msdyn365-commerce-modules/order-management/src/modules/order-history/./order-history';
import { SearchArea, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { ISalesOrder } from '@msdyn365-commerce-modules/order-management/src/modules/order-history/components/get-sales-order';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import { OrderShipments } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import { IOrderHistoryResources, IOrderHistoryConfig } from '../definition-extensions/order-history.ext.props.autogenerated';
import { getOrgUnitLocationsByAreaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';

/* VSI Customization - START */
interface ISalesOrderDetails {
    salesOrderDetails: OrderShipments;
}
// to pass props.context as an argument
interface IContext {
    context: ICoreContext;
}

interface IOrdersList {
    ordersList: OrderShipments[];
    config: IOrderHistoryConfig;
}

interface IOrderHistoryCustom {
    resources: IOrderHistoryResources;
    selectedOrderType: number;
    isMobile: boolean;
    salesOrderDetails?: OrderShipments;
    countGroups?: number;
    clubPlusId: number | undefined;
}

interface IHeaderWithState extends IHeader {
    selectedOrderType: number;
    setSelectedOrderType: React.Dispatch<React.SetStateAction<number>>;
}

interface IOrderTypeButtons {
    selectedOrderType: number;
    setSelectedOrderType: React.Dispatch<React.SetStateAction<number>>;
}

// Format any given number to currency
const _formatCurrency = (price: number | undefined, context: ICoreContext): string => {
    // If price is undefined, suppose it to be zero
    return context.cultureFormatter.formatCurrency(price ? price : 0, context.request.channel?.Currency);
};
/* VSI Customization - END - 27/01/21 */
export const OrderHistoryOrderInfomation: React.FC<IOrderInformation & IOrderHistoryCustom & ISalesOrderDetails & IContext> = ({
    orderInformationProps,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    context,
    resources: { orderPlaceLabel, orderLabel },
    salesOrderDetails,
    isMobile
}) => {
    /* VSI Customization - START */
    const totalAmount = salesOrderDetails.TotalAmount;
    const salesOrderTotal = _formatCurrency(totalAmount, context);
    // Check if total amount is 0, £0.00 will be dislayed instead of 'Free' label otherwise display amount as received in view props
    const salesOrderTotalAmount =
        totalAmount && totalAmount !== 0 ? (
            amount
        ) : (
            <Node className={'msc-price ms-order-history__order-information-amount'} tag='span'>
                {salesOrderTotal}
            </Node>
        );

    return (
        <Node {...orderInformationProps}>
            {channelName}
            {receiptId}
            <Node tag='div' className='prior-info row'>
                <div className={`${isMobile ? 'col-6' : ''}`}>
                    <Node tag='span' className='order-placed'>
                        <Node tag='label' className=''>
                            {/* {orderPlaceLabel} */}
                            Order Date:
                        </Node>
                        {createdDate}
                    </Node>
                    <Node tag='span' className='order-count'>
                        {count}
                    </Node>
                </div>
                <Node tag='span' className={`order-total ${isMobile ? 'col-6' : ''}`}>
                    <Node tag='label' className=''>
                        {orderLabel}
                    </Node>
                    {salesOrderTotalAmount}
                </Node>
            </Node>
            {/* {channelReferenceId} */}
        </Node>
    );
};

const _capitalizeFirstLetter = (status: string) => {
    if (status && status[0] && status[1]) {
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
    return status;
};

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery &
    IContext &
    IOrderHistoryCustom & {
        deliveryStatus: string;
        deliveryType: string;
        channelName: React.ReactNode;
        channelAddress: React.ReactNode;
    }> = ({
    deliveryProps,
    resources,
    deliveryType,
    deliveryStatus,
    address,
    isMobile,
    salesOrderDetails,
    countGroups,
    selectedOrderType,
    context,
    clubPlusId,
    channelName,
    channelAddress
}) => {
    const { salesIdInProcessText, transactionIdLabel, orderIdLabel } = resources;
    let salesID = salesOrderDetails && salesOrderDetails.SalesId;

    // If the order does not have a sales id, display the transaction id instead
    if (!salesID && salesOrderDetails?.ChannelId !== clubPlusId && salesOrderDetails?.ChannelId !== context.request.channel?.RecordId) {
        salesID = salesOrderDetails?.TransactionId;
    }

    const orderNo = (salesOrderDetails?.ChannelId === clubPlusId || salesOrderDetails?.ChannelId === context.request.channel?.RecordId) && (
        <Node className='order-information-sales-id' tag='span'>
            <b>
                {selectedOrderType === 0 ? orderIdLabel : transactionIdLabel}
                {` `}
            </b>
            {salesID !== undefined && salesID !== null && salesID !== '' ? salesID : salesIdInProcessText}
        </Node>
    );
    // // formate total amount to price with currency

    let FullAddress = '';
    let addressName = '';
    console.log(addressName);
    // @ts-ignore
    if (address && address.props && address.props && address.props.address && address.props.address.FullAddress) {
        // @ts-ignore
        FullAddress = address.props.address.FullAddress;
    }

    // @ts-ignore
    if (address && address.props && address.props && address.props.address && address.props.address.Name) {
        // @ts-ignore
        addressName = address.props.address.Name;
    }

    let groupSalesStatus = deliveryStatus && _capitalizeFirstLetter(deliveryStatus);
    // Add space between words before capital letter
    groupSalesStatus = groupSalesStatus.replace(/([A-Z])/g, ' $1').trim();

    // Tape
    if (groupSalesStatus === 'Canceled') {
        groupSalesStatus = 'Cancelled';
    }
    // Tape 2.0
    if (deliveryStatus.toLowerCase() === 'pickedup') {
        if (deliveryType.toLowerCase() === 'ship') {
            groupSalesStatus = 'Shipped';
        } else if (deliveryType.toLowerCase() === 'pickup') {
            groupSalesStatus = 'Picked Up';
        }
    } else if (deliveryStatus === 'readyForPickup') {
        if (deliveryType.toLowerCase() === 'ship') {
            groupSalesStatus = 'Preparing for dispatch';
        } else if (deliveryType.toLowerCase() === 'pickup') {
            groupSalesStatus = 'Ready For Pickup';
        }
    } else if (deliveryStatus === 'processing') {
        groupSalesStatus = 'Processing';
    }
    let addressHeading = '';
    if (countGroups && countGroups > 1) {
        addressHeading = deliveryType.toLowerCase() === 'ship' ? resources.deliveryAddressMultipleText : resources.storeAddressMultipleText;
    } else {
        addressHeading =
            deliveryType.toLowerCase() === 'ship'
                ? resources.deliveryAddressText
                : selectedOrderType === 0
                ? resources.storeAddressText
                : resources.inStoreAdress;
    }

    const [orgUnitLocationsData, setOrgUnitLocationsData] = React.useState<OrgUnitLocation[] | undefined>(undefined);
    const getOrgLoc = async (): Promise<void> => {
        // const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(0, 0, 0, 1, true);

        const searchArea: SearchArea = {
            Latitude: 0,
            Longitude: 0,
            Radius: 0,
            DistanceUnitValue: 0 // 0 is miles
        };
        const orgUnitLocations = await getOrgUnitLocationsByAreaAsync({ callerContext: context.actionContext }, searchArea);
        if (orgUnitLocations) {
            setOrgUnitLocationsData(orgUnitLocations);
        }
    };

    React.useEffect(() => {
        getOrgLoc();
    }, []);

    const getStoreName = () => {
        let storeName = '';
        orgUnitLocationsData &&
            orgUnitLocationsData.map(s => {
                // @ts-ignore
                if (s.PostalAddressId === address?.props?.address?.RecordId) {
                    storeName = `Dobbies ${s.OrgUnitName}`;
                }
            });

        if (storeName === '') {
            // @ts-ignore
            storeName = channelAddress?.props?.children?.toString().slice(0, -4);
        }

        return storeName;
    };

    return (
        <Node {...deliveryProps}>
            {salesOrderDetails?.ChannelId !== clubPlusId && (
                <Node tag='div' className={`delivery-mode`}>
                    <Node tag='span' className=''>
                        <b>{addressHeading}</b>{' '}
                    </Node>
                    <Node tag='span' className={`__group-delivery-heading`}>
                        {/* {FullAddress === '' ? FullAddress : FullAddress.slice(0, FullAddress.length - 4)} */}
                        {/* {
                        selectedOrderType == 0 ? (
                            FullAddress === '' ? FullAddress : FullAddress.slice(0, FullAddress.length - 4)
                        ) : (
                            deliveryType.toLowerCase() === 'ship' ? FullAddress === '' ? FullAddress : FullAddress.slice(0, FullAddress.length - 4) : addressName
                        )
                    } */}
                        {deliveryType.toLowerCase() === 'ship'
                            ? FullAddress === ''
                                ? FullAddress
                                : FullAddress.slice(0, FullAddress.length - 4)
                            : getStoreName()}
                    </Node>
                </Node>
            )}

            <div className={isMobile ? 'row' : ''}>
                <Node tag='div' className={`order-number ${isMobile ? 'col-6' : ''}`}>
                    {orderNo}
                </Node>

                <Node tag='div' className={`status-tracking-info ${isMobile ? 'col-6' : ''}`}>
                    {selectedOrderType === 0 ? (
                        <>
                            <Node tag='b' className=''>
                                {resources.orderStatusLabel}{' '}
                            </Node>
                            {groupSalesStatus}
                        </>
                    ) : null}
                </Node>
            </div>
            {/* VSI-Customization -- END  */}
        </Node>
    );
};

export const OrderHistoryGroup: React.FC<IGroup &
    IOrderHistoryCustom &
    IContext & { channelName: React.ReactNode; channelAddress: React.ReactNode }> = props => {
    const {
        groupProps,
        delivery,
        salesLinesProps,
        salesLines,
        resources,
        data,
        selectedOrderType,
        isMobile,
        salesOrderDetails,
        countGroups,
        clubPlusId,
        channelName,
        channelAddress
    } = props;
    const groupDeliveryInfo = {
        deliveryStatus: data.salesStatus.toString(),
        deliveryType: data.deliveryType.toString()
    };
    return (
        <Node {...groupProps}>
            {delivery && (
                <OrderHistoryGroupDelivery
                    resources={resources}
                    {...delivery}
                    {...groupDeliveryInfo}
                    selectedOrderType={selectedOrderType}
                    isMobile={isMobile}
                    salesOrderDetails={salesOrderDetails}
                    countGroups={countGroups}
                    context={props.context}
                    clubPlusId={clubPlusId}
                    channelName={channelName}
                    channelAddress={channelAddress}
                />
            )}
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map(salesLine => (
                        <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                    ))}
                </Node>
            )}
        </Node>
    );
};

export const OrderHistoryGroups: React.FC<IGroups &
    IOrderHistoryCustom &
    IContext & { channelName: React.ReactNode; channelAddress: React.ReactNode }> = ({
    groupsProps,
    groups,
    resources,
    selectedOrderType,
    isMobile,
    salesOrderDetails,
    context,
    clubPlusId,
    channelName,
    channelAddress
}) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderHistoryGroup
                salesOrderDetails={salesOrderDetails}
                isMobile={isMobile}
                resources={resources}
                key={index}
                {...group}
                selectedOrderType={selectedOrderType}
                countGroups={groups.length}
                context={context}
                clubPlusId={clubPlusId}
                channelName={channelName}
                channelAddress={channelAddress}
            />
        ))}
    </Node>
);

const OrderTypeButtons: React.FC<IOrderTypeButtons> = ({ selectedOrderType, setSelectedOrderType }) => {
    return (
        <div className='row ms-order-details-btns'>
            <button
                onClick={() => setSelectedOrderType(0)}
                className={`msc-btn ms-order-details__btn-${selectedOrderType === 0 ? 'black' : 'white'}`}
            >
                Online orders
            </button>

            <button
                onClick={() => setSelectedOrderType(1)}
                className={`msc-btn ms-order-details__btn-${selectedOrderType === 1 ? 'black' : 'white'}`}
            >
                In-store receipts
            </button>
        </div>
    );
};

export const OrderHistoryHeader: React.FC<IHeaderWithState> = ({ headerProps, heading, selectedOrderType, setSelectedOrderType }) => (
    <Node {...headerProps}>
        <OrderTypeButtons selectedOrderType={selectedOrderType} setSelectedOrderType={setSelectedOrderType} />
        {heading}
        {/* orderCountLabel */} {/* VSI Customization */}
    </Node>
);

export const OrderHistorySalesOder: React.FC<ISalesOrder & IOrderHistoryCustom & ISalesOrderDetails & IContext> = props => {
    const {
        salesOrderProps,
        orderInfomation,
        groups,
        orderDetailsLink,
        resources,
        salesOrderDetails,
        context,
        selectedOrderType,
        isMobile,
        clubPlusId
    } = props;

    return (
        <Node {...salesOrderProps}>
            {orderInfomation && (
                <OrderHistoryOrderInfomation
                    selectedOrderType={selectedOrderType}
                    resources={resources}
                    {...orderInfomation}
                    salesOrderDetails={salesOrderDetails}
                    context={context}
                    isMobile={isMobile}
                    clubPlusId={clubPlusId}
                />
            )}
            {groups && (
                <OrderHistoryGroups
                    isMobile={isMobile}
                    selectedOrderType={selectedOrderType}
                    resources={resources}
                    salesOrderDetails={salesOrderDetails}
                    {...groups}
                    context={context}
                    clubPlusId={clubPlusId}
                    {...orderInfomation}
                />
            )}
            <div className='orderhistory-cta'>
                {orderDetailsLink}{' '}
                <a
                    href={orderDetailsLink!['props'].href}
                    role='button'
                    aria-label='View order details'
                    data-exp-event-id='Order-history.Order history.Order details.click'
                    className='ms-order-history__btn-order-details msc-btn buyitem'
                >
                    Buy items again
                </a>
            </div>
        </Node>
    );
};

export const OrderHistoryList: React.FC<IList & IOrderHistoryCustom & IOrdersList & IContext> = props => {
    const { listProps, salesOrders, resources, ordersList, context, selectedOrderType, isMobile, config, clubPlusId } = props;

    return (
        <Node {...listProps}>
            {salesOrders &&
                salesOrders.map((salesOrder, index) => {
                    const order = ordersList[index];
                    let isOrderCancelled = false;
                    salesOrder.groups?.groups.map(grp => {
                        if (grp.data.salesStatus.toLowerCase() === 'canceled') {
                            isOrderCancelled = true;
                        }
                    });
                    if (isOrderCancelled) {
                        return <></>;
                    }
                    if (
                        selectedOrderType === 0 &&
                        (order.ChannelId === config.clubPlusId || order.ChannelId === props.context.request.channel?.RecordId)
                    ) {
                        return (
                            <OrderHistorySalesOder
                                key={index}
                                {...salesOrder}
                                resources={resources}
                                salesOrderDetails={ordersList[index]}
                                context={context}
                                selectedOrderType={selectedOrderType}
                                isMobile={isMobile}
                                clubPlusId={clubPlusId}
                            />
                        );
                    }

                    if (
                        selectedOrderType === 1 &&
                        order.ChannelId !== config.clubPlusId &&
                        order.ChannelId !== props.context.request.channel?.RecordId
                    ) {
                        return (
                            <OrderHistorySalesOder
                                key={index}
                                {...salesOrder}
                                resources={resources}
                                salesOrderDetails={ordersList[index]}
                                context={context}
                                selectedOrderType={selectedOrderType}
                                isMobile={isMobile}
                                clubPlusId={clubPlusId}
                            />
                        );
                    }
                    return null;
                })}
        </Node>
    );
};

const OrderHistoryView: React.FC<IOrderHistoryViewProps & IOrderHistoryCustom & IOrdersList> = props => {
    const {
        orderHistoryProps,
        orderHistory: { salesOrders },
        header,
        alert,
        loading,
        emptyMessage,
        backToShoppingLink,
        list,
        moreButton,
        resources,
        config
    } = props;
    const isMobileView = (): boolean => {
        if (typeof window !== undefined && window.innerWidth) {
            const gridSettings = props.context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth < gridSettings.xs.w) {
                    return true;
                } else if (gridSettings.sm && window.innerWidth < gridSettings.sm.w) {
                    return true;
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return false;
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return false;
                } else {
                    return false;
                }
            }
        }
        return false;
    };

    // 0 for online store, 1 for in store transactions
    const [selectedOrderType, setSelectedOrderType] = React.useState(0);
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const isMbl = isMobileView();
        setIsMobile(isMbl);
    }, []);

    return (
        <Module {...orderHistoryProps}>
            {header && <OrderHistoryHeader {...header} selectedOrderType={selectedOrderType} setSelectedOrderType={setSelectedOrderType} />}
            {loading}
            {alert && (
                <>
                    {alert}
                    {backToShoppingLink}
                </>
            )}
            {emptyMessage && (
                <>
                    {emptyMessage}
                    {backToShoppingLink}
                </>
            )}
            <Node tag='span' className='order-history-top-text'>
                {resources.orderHistoryText}
            </Node>
            {list && (
                <OrderHistoryList
                    resources={resources}
                    {...list}
                    ordersList={salesOrders}
                    context={props.context}
                    selectedOrderType={selectedOrderType}
                    isMobile={isMobile}
                    config={config}
                    clubPlusId={config.clubPlusId}
                />
            )}
            {moreButton && moreButton}
        </Module>
    );
};

export default OrderHistoryView;
